/**
 * InquiriesTable Component
 * 
 * Structured table for viewing and managing credit inquiries.
 * Supports single and bulk dispute actions with AI-powered caution detection.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Clock,
  Building,
  AlertTriangle,
  Plus,
  CheckCircle,
  Filter,
  Gavel,
  X,
  ShieldAlert,
  Link2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { analyzeInquiries, INQUIRY_RECOMMENDATION, RECOMMENDATION_LABELS, RECOMMENDATION_COLORS } from '../../utils/inquiryAnalysis';
import { InquiryWarningModal, BulkInquiryWarningModal } from '../disputes/InquiryWarningModal';

/**
 * AI Recommendation Badge Component - Five S Smart Analysis
 */
const AIRecommendationBadge = ({ analysis, theme }) => {
  if (!analysis) return null;
  
  const recommendation = analysis.recommendation || INQUIRY_RECOMMENDATION.NEEDS_REVIEW;
  const label = RECOMMENDATION_LABELS[recommendation] || 'Review';
  const colors = RECOMMENDATION_COLORS[recommendation] || RECOMMENDATION_COLORS[INQUIRY_RECOMMENDATION.NEEDS_REVIEW];
  
  return (
    <div className="flex flex-col gap-1">
      <span 
        className="text-xs px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1"
        style={{ 
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
        }}
        data-testid="ai-recommendation-badge"
      >
        {recommendation === INQUIRY_RECOMMENDATION.LIKELY_LEGITIMATE && <CheckCircle size={10} />}
        {recommendation === INQUIRY_RECOMMENDATION.NEEDS_REVIEW && <AlertTriangle size={10} />}
        {recommendation === INQUIRY_RECOMMENDATION.POSSIBLE_DUPLICATE && <Link2 size={10} />}
        {label}
      </span>
      {analysis.reason && (
        <span 
          className="text-xs max-w-[180px] line-clamp-2"
          style={{ color: theme?.text?.muted || '#6B7280' }}
          title={analysis.reason}
        >
          {analysis.reason}
        </span>
      )}
    </div>
  );
};

/**
 * Bureau Badge Component
 */
const BureauBadge = ({ bureau }) => {
  const getBureauDisplay = (code) => {
    if (!code) return null;
    const normalized = code.toUpperCase();
    if (normalized === 'EX' || normalized.includes('EXPERIAN')) return { name: 'Experian', color: 'bg-emerald-100 text-emerald-700' };
    if (normalized === 'TU' || normalized.includes('TRANSUNION')) return { name: 'TransUnion', color: 'bg-amber-100 text-amber-700' };
    if (normalized === 'EQ' || normalized.includes('EQUIFAX')) return { name: 'Equifax', color: 'bg-red-100 text-red-700' };
    return { name: code, color: 'bg-slate-100 text-slate-600' };
  };
  
  const display = getBureauDisplay(bureau);
  if (!display) return <span className="text-slate-400 text-sm">--</span>;
  
  return (
    <span className={cn('text-xs px-2 py-1 rounded-full font-medium', display.color)}>
      {display.name}
    </span>
  );
};

/**
 * Empty State Component - Themed
 */
const EmptyState = ({ title, description, theme = {}, isPositive = false }) => (
  <div className="text-center py-12 px-6">
    <div 
      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
      style={{ 
        background: isPositive ? theme?.positiveBox?.bg : theme?.emptyIcon?.bg || 'rgba(15,172,108,0.10)',
        border: `1px solid ${isPositive ? theme?.positiveBox?.border : theme?.emptyIcon?.border || 'rgba(15,172,108,0.12)'}`,
      }}
    >
      <Search size={24} style={{ color: isPositive ? theme?.positiveBox?.color : theme?.emptyIcon?.color || '#0D9F64' }} />
    </div>
    <h3 
      className="text-lg font-bold mb-2"
      style={{ color: theme?.text?.primary || '#111827' }}
    >
      {title}
    </h3>
    <p 
      className="text-sm max-w-md mx-auto"
      style={{ color: theme?.text?.muted || '#6B7280' }}
    >
      {description}
    </p>
  </div>
);

/**
 * Bulk Action Bar Component
 */
const BulkActionBar = ({ selectedCount, onDispute, onClear }) => {
  if (selectedCount === 0) return null;
  
  return (
    <div className="sticky bottom-0 left-0 right-0 p-4 bg-stone-800 rounded-xl shadow-lg border border-slate-700 flex items-center justify-between animate-in slide-in-from-bottom-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
          <CheckCircle size={20} className="text-white" />
        </div>
        <div>
          <p className="text-white font-medium">{selectedCount} inquir{selectedCount === 1 ? 'y' : 'ies'} selected</p>
          <p className="text-slate-400 text-sm">Ready for bulk action</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onClear}
          className="text-slate-300 hover:text-white hover:bg-slate-700"
          data-testid="clear-selection"
        >
          <X size={16} className="mr-1" />
          Clear
        </Button>
        <Button
          onClick={onDispute}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="bulk-dispute-btn"
        >
          <Gavel size={16} className="mr-2" />
          Dispute Selected
        </Button>
      </div>
    </div>
  );
};

/**
 * Main InquiriesTable Component
 */
export const InquiriesTable = ({ inquiries = [], tradelines = [], bureauCounts = {}, theme = {} }) => {
  const navigate = useNavigate();
  
  // State
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [bureauFilter, setBureauFilter] = useState('all');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showBulkWarningModal, setShowBulkWarningModal] = useState(false);
  const [pendingInquiry, setPendingInquiry] = useState(null);
  const [pendingBulkInquiries, setPendingBulkInquiries] = useState({ safe: [], caution: [] });
  
  // Enrich inquiries with strict bureau-aware legitimacy matching
  const enrichedInquiries = useMemo(() => {
    const analyzed = analyzeInquiries(inquiries, tradelines);
    return analyzed.all.map((inquiry, idx) => ({
      ...inquiry,
      inquiry_id: inquiry.inquiry_id || inquiry.id || `inq-${idx}`,
    }));
  }, [inquiries, tradelines]);
  
  // Compute actual bureau counts from inquiries data
  const actualBureauCounts = useMemo(() => {
    const counts = { transunion: 0, experian: 0, equifax: 0, unknown: 0 };
    enrichedInquiries.forEach(inq => {
      const bureau = (inq.bureau_code || inq.bureau || '').toUpperCase();
      if (bureau === 'TU' || bureau.includes('TRANSUNION')) counts.transunion++;
      else if (bureau === 'EX' || bureau.includes('EXPERIAN')) counts.experian++;
      else if (bureau === 'EQ' || bureau.includes('EQUIFAX')) counts.equifax++;
      else counts.unknown++;
    });
    return counts;
  }, [enrichedInquiries]);
  
  // Total inquiries count
  const totalInquiries = enrichedInquiries.length;
  
  // Filter inquiries
  const filteredInquiries = useMemo(() => {
    let filtered = enrichedInquiries;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(inq => 
        (inq.creditor_name || inq.company_name || inq.creditor || '').toLowerCase().includes(query) ||
        (inq.type || inq.business_type || '').toLowerCase().includes(query)
      );
    }
    
    // Bureau filter
    if (bureauFilter !== 'all') {
      filtered = filtered.filter(inq => {
        const bureau = (inq.bureau_code || inq.bureau || '').toUpperCase();
        if (bureauFilter === 'transunion') return bureau === 'TU' || bureau.includes('TRANSUNION');
        if (bureauFilter === 'experian') return bureau === 'EX' || bureau.includes('EXPERIAN');
        if (bureauFilter === 'equifax') return bureau === 'EQ' || bureau.includes('EQUIFAX');
        return true;
      });
    }
    
    return filtered;
  }, [enrichedInquiries, searchQuery, bureauFilter]);
  
  // Selection handlers
  const isAllSelected = filteredInquiries.length > 0 && 
    filteredInquiries.every(inq => selectedIds.has(inq.inquiry_id));
  
  const isSomeSelected = filteredInquiries.some(inq => selectedIds.has(inq.inquiry_id));
  
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredInquiries.map(inq => inq.inquiry_id)));
    }
  }, [isAllSelected, filteredInquiries]);
  
  const toggleSelect = useCallback((inquiryId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(inquiryId)) {
        next.delete(inquiryId);
      } else {
        next.add(inquiryId);
      }
      return next;
    });
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);
  
  // Get selected inquiries
  const selectedInquiries = useMemo(() => {
    return filteredInquiries.filter(inq => selectedIds.has(inq.inquiry_id));
  }, [filteredInquiries, selectedIds]);
  
  // Handle single inquiry dispute
  const handleDisputeSingle = useCallback((inquiry) => {
    if (inquiry.analysis?.isCaution) {
      setPendingInquiry(inquiry);
      setShowWarningModal(true);
    } else {
      // Navigate directly to dispute with Data Breach method (default for inquiries)
      navigate('/dispute-wizard/factual/select-items', { 
        state: { 
          preloadedItems: {
            inquiries: [inquiry],
            accounts: [],
            publicRecords: [],
            personalInfo: [],
          },
          fromSmartRouting: true,
          classification: 'data_breach',
          recommendedStrategy: {
            title: 'Data Breach Method',
            key: 'data_breach_method',
            reason: 'Inquiries default to Data Breach Method for unauthorized inquiry removal.',
          },
          selectedMethod: 'data_breach',
          disputeType: 'inquiry',
          targetBureaus: [inquiry.bureau || 'All Bureaus'],
        } 
      });
    }
  }, [navigate]);
  
  // Handle single warning proceed
  const handleSingleProceed = useCallback(() => {
    setShowWarningModal(false);
    if (pendingInquiry) {
      navigate('/dispute-wizard/factual/select-items', { 
        state: { 
          preloadedItems: {
            inquiries: [pendingInquiry],
            accounts: [],
            publicRecords: [],
            personalInfo: [],
          },
          fromSmartRouting: true,
          classification: 'data_breach',
          recommendedStrategy: {
            title: 'Data Breach Method',
            key: 'data_breach_method',
            reason: 'Inquiries default to Data Breach Method for unauthorized inquiry removal.',
          },
          selectedMethod: 'data_breach',
          disputeType: 'inquiry',
        } 
      });
    }
    setPendingInquiry(null);
  }, [pendingInquiry, navigate]);
  
  // Handle bulk dispute
  const handleBulkDispute = useCallback(() => {
    const safe = selectedInquiries.filter(inq => !inq.analysis?.isCaution);
    const caution = selectedInquiries.filter(inq => inq.analysis?.isCaution);
    
    if (caution.length > 0) {
      setPendingBulkInquiries({ safe, caution });
      setShowBulkWarningModal(true);
    } else {
      // All safe - proceed directly with Data Breach method
      navigate('/dispute-wizard/factual/select-items', { 
        state: { 
          preloadedItems: {
            inquiries: selectedInquiries,
            accounts: [],
            publicRecords: [],
            personalInfo: [],
          },
          fromSmartRouting: true,
          classification: 'data_breach',
          recommendedStrategy: {
            title: 'Data Breach Method',
            key: 'data_breach_method',
            reason: `${selectedInquiries.length} inquir${selectedInquiries.length === 1 ? 'y' : 'ies'} selected. Inquiries default to Data Breach Method.`,
          },
          selectedMethod: 'data_breach',
          disputeType: 'inquiry_bulk',
        } 
      });
    }
  }, [selectedInquiries, navigate]);
  
  // Handle bulk dispute safe only
  const handleBulkDisputeSafe = useCallback(() => {
    setShowBulkWarningModal(false);
    if (pendingBulkInquiries.safe.length > 0) {
      navigate('/dispute-wizard/factual/select-items', { 
        state: { 
          preloadedItems: {
            inquiries: pendingBulkInquiries.safe,
            accounts: [],
            publicRecords: [],
            personalInfo: [],
          },
          fromSmartRouting: true,
          classification: 'data_breach',
          recommendedStrategy: {
            title: 'Data Breach Method',
            key: 'data_breach_method',
            reason: `${pendingBulkInquiries.safe.length} inquir${pendingBulkInquiries.safe.length === 1 ? 'y' : 'ies'} selected (safe only). Inquiries default to Data Breach Method.`,
          },
          selectedMethod: 'data_breach',
          disputeType: 'inquiry_bulk',
        } 
      });
    }
    setPendingBulkInquiries({ safe: [], caution: [] });
    clearSelection();
  }, [pendingBulkInquiries, navigate, clearSelection]);
  
  // Handle bulk dispute all
  const handleBulkDisputeAll = useCallback(() => {
    setShowBulkWarningModal(false);
    const all = [...pendingBulkInquiries.safe, ...pendingBulkInquiries.caution];
    if (all.length > 0) {
      navigate('/dispute-wizard/factual/select-items', { 
        state: { 
          preloadedItems: {
            inquiries: all,
            accounts: [],
            publicRecords: [],
            personalInfo: [],
          },
          fromSmartRouting: true,
          classification: 'data_breach',
          recommendedStrategy: {
            title: 'Data Breach Method',
            key: 'data_breach_method',
            reason: `${all.length} inquir${all.length === 1 ? 'y' : 'ies'} selected (including caution). Inquiries default to Data Breach Method.`,
          },
          selectedMethod: 'data_breach',
          disputeType: 'inquiry_bulk',
        } 
      });
    }
    setPendingBulkInquiries({ safe: [], caution: [] });
    clearSelection();
  }, [pendingBulkInquiries, navigate, clearSelection]);
  
  if (inquiries.length === 0) {
    return (
      <EmptyState
        title="No Inquiries Found"
        description="No credit inquiries were extracted from your report."
        theme={theme}
        isPositive={true}
      />
    );
  }
  
  return (
    <div className="space-y-4" data-testid="inquiries-table">
      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div 
            className="relative flex items-center h-10 px-3 rounded-xl w-64"
            style={{
              background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
              border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
              backdropFilter: 'blur(12px)',
            }}
          >
            <Search size={15} className="flex-shrink-0" style={{ color: theme?.icon?.neutral || '#5F6F64' }} />
            <input
              type="text"
              placeholder="Search inquiries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none ml-2.5 text-sm"
              style={{ color: theme?.input?.text || '#111827' }}
              data-testid="inquiry-search"
            />
          </div>
          
          {/* Bureau Filter */}
          <Select value={bureauFilter} onValueChange={setBureauFilter}>
            <SelectTrigger 
              className="h-10 px-3 w-36 font-semibold text-sm"
              style={{
                background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
                border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
                borderRadius: '12px',
                color: theme?.text?.secondary || '#4B5563',
                backdropFilter: 'blur(12px)',
              }}
              data-testid="bureau-filter"
            >
              <Filter size={14} className="mr-2 flex-shrink-0" style={{ color: theme?.icon?.neutral || '#5F6F64' }} />
              <SelectValue placeholder="All Bureaus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bureaus</SelectItem>
              <SelectItem value="transunion">TransUnion</SelectItem>
              <SelectItem value="experian">Experian</SelectItem>
              <SelectItem value="equifax">Equifax</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div 
          className="text-sm"
          style={{ color: theme?.text?.muted || '#6B7280' }}
        >
          {filteredInquiries.length === totalInquiries 
            ? `${totalInquiries} inquir${totalInquiries === 1 ? 'y' : 'ies'}`
            : `${filteredInquiries.length} of ${totalInquiries} inquir${totalInquiries === 1 ? 'y' : 'ies'}`
          }
        </div>
      </div>
      
      {/* Bureau Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {['transunion', 'experian', 'equifax'].map(bureau => (
          <div 
            key={bureau} 
            className="text-center p-3 rounded-xl"
            style={{
              background: theme?.surface?.s2 || 'rgba(255,255,255,0.76)',
              border: `1px solid ${theme?.border?.b2 || 'rgba(15,172,108,0.12)'}`,
            }}
          >
            <p 
              className="text-xl font-bold"
              style={{ color: theme?.metric?.value || '#111827' }}
            >
              {actualBureauCounts[bureau] ?? 0}
            </p>
            <p 
              className="text-xs capitalize"
              style={{ color: theme?.metric?.label || '#6B7280' }}
            >
              {bureau}
            </p>
          </div>
        ))}
      </div>
      
      {/* Show total if there are unclassified inquiries */}
      {actualBureauCounts.unknown > 0 && (
        <div className="text-xs mb-2" style={{ color: theme?.text?.muted || '#6B7280' }}>
          Note: {actualBureauCounts.unknown} inquir{actualBureauCounts.unknown === 1 ? 'y' : 'ies'} without bureau classification
        </div>
      )}
      
      {/* Table */}
      {filteredInquiries.length > 0 ? (
        <div 
          className="overflow-x-auto rounded-xl"
          style={{
            background: theme?.table?.container || 'rgba(255,255,255,0.84)',
            border: `1px solid ${theme?.border?.b1 || 'rgba(255,255,255,0.44)'}`,
          }}
        >
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: theme?.table?.header || 'rgba(255,255,255,0.60)' }}>
                <th 
                  className="text-center py-3 px-3 w-12"
                  style={{ borderBottom: `1px solid ${theme?.table?.border || 'rgba(15,172,108,0.10)'}` }}
                >
                  <Checkbox 
                    checked={isAllSelected}
                    indeterminate={isSomeSelected && !isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="data-[state=checked]:bg-emerald-600 data-[state=indeterminate]:bg-emerald-600"
                    data-testid="select-all-inquiries"
                  />
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Creditor Name
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Type of Business
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Date of Inquiry
                </th>
                <th 
                  className="text-center py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Credit Bureau
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  AI Recommendation
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredInquiries.map((inquiry, idx) => {
                const isSelected = selectedIds.has(inquiry.inquiry_id);
                const isCaution = inquiry.analysis?.isCaution;
                const rowBg = isSelected 
                  ? theme?.table?.rowHover 
                  : idx % 2 === 0 
                    ? theme?.table?.row 
                    : theme?.table?.rowAlt;
                
                return (
                  <tr 
                    key={inquiry.inquiry_id}
                    className="transition-colors cursor-pointer hover:opacity-90"
                    style={{
                      background: rowBg,
                      borderBottom: `1px solid ${theme?.table?.border}`,
                    }}
                    data-testid={`inquiry-row-${inquiry.inquiry_id}`}
                    onClick={(e) => {
                      // Full-row selection: toggle selection when clicking on the row
                      // Exclude clicks on action buttons, checkboxes, and links
                      const target = e.target;
                      const isButton = target.closest('button');
                      const isCheckbox = target.closest('[role="checkbox"]');
                      const isLink = target.closest('a');
                      
                      // If the click is on an action button/checkbox/link, don't toggle row selection
                      if (isButton || isCheckbox || isLink) {
                        return;
                      }
                      
                      toggleSelect(inquiry.inquiry_id);
                    }}
                  >
                    {/* Select */}
                    <td className="py-4 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(inquiry.inquiry_id)}
                        className="data-[state=checked]:bg-emerald-600"
                      />
                    </td>
                    
                    {/* Creditor Name */}
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ 
                            background: isCaution 
                              ? (theme?.tag?.gold?.bg || 'rgba(234,178,58,0.16)') 
                              : (theme?.iconBox?.bg || 'rgba(15,172,108,0.10)') 
                          }}
                        >
                          {isCaution ? (
                            <ShieldAlert size={18} style={{ color: theme?.icon?.warn || '#EAB23A' }} />
                          ) : (
                            <Search size={18} style={{ color: theme?.icon?.primary || '#0D9F64' }} />
                          )}
                        </div>
                        <p 
                          className="font-semibold"
                          style={{ color: theme?.text?.primary }}
                        >
                          {inquiry.creditor_name || inquiry.company_name || inquiry.creditor || 'Unknown'}
                        </p>
                      </div>
                    </td>
                    
                    {/* Type of Business */}
                    <td className="py-4 px-4">
                      <span 
                        className="text-sm"
                        style={{ color: theme?.text?.secondary }}
                      >
                        {inquiry.type || inquiry.business_type || '--'}
                      </span>
                    </td>
                    
                    {/* Date */}
                    <td className="py-4 px-4">
                      <div 
                        className="flex items-center gap-1 text-sm"
                        style={{ color: theme?.text?.secondary }}
                      >
                        <Clock size={14} style={{ color: theme?.icon?.neutral }} />
                        {inquiry.inquiry_date || inquiry.date || '--'}
                      </div>
                    </td>
                    
                    {/* Bureau */}
                    <td className="py-4 px-4 text-center">
                      <span 
                        className="text-xs px-2 py-1 rounded-full font-semibold"
                        style={{ 
                          background: theme?.bureauPill?.bg,
                          border: `1px solid ${theme?.bureauPill?.border}`,
                          color: theme?.bureauPill?.color,
                        }}
                      >
                        {inquiry.bureau_code || inquiry.bureau || '--'}
                      </span>
                    </td>
                    
                    {/* AI Recommendation */}
                    <td className="py-4 px-4">
                      <AIRecommendationBadge analysis={inquiry.analysis} theme={theme} />
                    </td>
                    
                    {/* Actions */}
                    <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisputeSingle(inquiry)}
                        data-testid={`dispute-inquiry-${inquiry.inquiry_id}`}
                        style={{ color: isCaution ? theme?.icon?.warn : theme?.icon?.primary }}
                      >
                        {isCaution ? <ShieldAlert size={16} /> : <Plus size={16} />}
                        <span className="ml-1">Dispute</span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div 
          className="text-center py-8"
          style={{ color: theme?.text?.muted }}
        >
          No inquiries match your search criteria.
        </div>
      )}
      
      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        onDispute={handleBulkDispute}
        onClear={clearSelection}
      />
      
      {/* Single Inquiry Warning Modal */}
      <InquiryWarningModal
        open={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
          setPendingInquiry(null);
        }}
        onProceed={handleSingleProceed}
        inquiry={pendingInquiry}
        linkedAccount={pendingInquiry?.analysis?.linkedAccount}
      />
      
      {/* Bulk Warning Modal */}
      <BulkInquiryWarningModal
        open={showBulkWarningModal}
        onClose={() => {
          setShowBulkWarningModal(false);
          setPendingBulkInquiries({ safe: [], caution: [] });
        }}
        onDisputeSafe={handleBulkDisputeSafe}
        onDisputeAll={handleBulkDisputeAll}
        safeInquiries={pendingBulkInquiries.safe}
        cautionInquiries={pendingBulkInquiries.caution}
      />
    </div>
  );
};

export default InquiriesTable;
